import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  Avatar,
  Modal,
  Drawer,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {
  Notifications,
  Dashboard,
  People,
  AccessTime,
  Gavel,
  Menu as MenuIcon,
  CalendarToday
} from "@mui/icons-material";
import EditProfile from "../../../components/EditProfile";
import AttendanceTimerWithoutTimers from "../../../components/AttendanceTimerWithoutTimers";
import EmployeeManagement from "../EmployeeManagement/EmployeeManagement";
import AttendanceManagement from "../Attendance/Attendance";
import HRHomeDashboard from './HRHomeDashboard';
import HRLeaveDashboard from "../newLeave/implementedLeaveManagement/Dashboard";
import HRPerformanceReviewDashboard from '../Performance_HR/HRPerformanceReviewDashboard '
import hrmsDashboardImage from '../../../assets/Hmrs_Hashstack.png'
import PushNotificationManager from "../../../components/PushNotificationPrompt";
import { axiosInstance, localAxiosGet } from "../../../Axios/axios";

const HRDashboard = () => {
  const [status, setStatus] = useState('OUT');
  const [userDetails, setUserDetails] = useState(null);
  const [empId, setEmpId] = useState(null);
  const [empOrgId, setEmpOrgId] = useState(null);

  // Responsive drawer state
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: 800,
    bgcolor: 'background.paper',
    border: '1px solid #000',
    boxShadow: 24,
    p: 4,
    borderRadius: 8,
  };

  const [openProfile, setOpenProfile] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState("Dashboard");

  const handleOpenProfile = () => {
    setOpenProfile(true);
  };

  const handleCloseProfile = () => {
    setOpenProfile(false);
  };

  // Handle drawer toggle for responsive design
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Helper function to get initials from name
  const getInitials = (firstName, lastName) => {
    let initials = '';
    
    // Get first character from first name
    if (firstName && firstName.trim()) {
      // Extract first character of the first word in first_name
      const firstPart = firstName.trim().split(' ')[0];
      initials += firstPart.charAt(0).toUpperCase();
    }
    
    // Get first character from last name
    if (lastName && lastName.trim()) {
      initials += lastName.charAt(0).toUpperCase();
    } else if (firstName && firstName.trim()) {
      // If no last name, try to get second word from first_name field
      const nameParts = firstName.trim().split(' ');
      if (nameParts.length > 1) {
        initials += nameParts[nameParts.length - 1].charAt(0).toUpperCase();
      }
    }
    
    return initials || 'U'; // Return 'U' for User if no initials found
  };

  // Helper function to get full name for display
  const getFullName = () => {
    if (!userDetails) return '';
    
    if (userDetails.first_name && userDetails.last_name) {
      return `${userDetails.first_name} ${userDetails.last_name}`;
    }
    
    return userDetails.first_name || 'User';
  };

  const fetchUserDetails = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axiosInstance(
        localAxiosGet(`/organization/getUserDetailsByUserId?userId=${empId}`, accessToken)
      );
      
      console.log("API Response:", response.data);
      if (response.data.status === "SUCCESS") {
        const userData = response.data.message.user;
        setUserDetails(userData);
        localStorage.setItem("userDetails", JSON.stringify(userData));
        console.log('User details fetched successfully:', userData);
      }
    } catch (error) {
      console.log('Error in Fetching User details', error);
    }
  };

  const fetchNotificationCount = async () => {
    if (!empId) return;

    try {
      const response = await axiosInstance(
        localAxiosGet(`/notifications/count?user_id=${empId}`)
      );

      if (response.data.status === "SUCCESS") {
        setNotificationCount(response.data.message.count);
      }
    } catch (error) {
      console.log("Error fetching notification count:", error);
    }
  };

  const fetchNotifications = async () => {
    if (!empId) return;

    try {
      const response = await axiosInstance(
        localAxiosGet(`/notifications/getNotifications?userId=${empId}`)
      );

      if (response.data.status === "SUCCESS") {
        setNotifications(response.data.message);
      }
    } catch (error) {
      console.log("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    const employeid = localStorage.getItem("userId");
    const employeorgid = localStorage.getItem("organizationId");
    
    if (employeid) {
      setEmpId(employeid);
    }
    if (employeorgid) {
      setEmpOrgId(employeorgid);
    }

    // Try to get user details from localStorage first
    const storedUserDetails = localStorage.getItem("userDetails");
    if (storedUserDetails) {
      try {
        setUserDetails(JSON.parse(storedUserDetails));
      } catch (error) {
        console.error("Error parsing stored user details:", error);
      }
    }

    console.log("Dashboard loaded with User ID:", employeid);
    console.log("Dashboard loaded with Organization ID:", employeorgid);
  }, []);

  // Fetch user details when empId is available
  useEffect(() => {
    if (empId) {
      fetchUserDetails();
      fetchNotificationCount();
      
      // Set up interval for notification count refresh
      const intervalId = setInterval(fetchNotificationCount, 30 * 60 * 1000);
      return () => clearInterval(intervalId);
    }
  }, [empId]);

  const currentDate = new Date().toLocaleDateString();

  const handleNotificationClick = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleNotificationClose = () => {
    setAnchorEl(null);
  };

  // Menu item selection handler that also closes drawer on mobile
  const handleMenuSelect = (menuName) => {
    setSelectedMenu(menuName);
    if (isSmallScreen) {
      setMobileOpen(false);
    }
  };

  const menuItems = [
    { name: "Dashboard", icon: <Dashboard />, component: <HRHomeDashboard setSelectedMenu={setSelectedMenu} /> },
    { name: "Employee Management", icon: <People />, component: <EmployeeManagement selectedMenu={setSelectedMenu} /> },
    { name: "Attendance Management", icon: <AccessTime />, component: <AttendanceManagement selectedMenu={setSelectedMenu} /> },
    { name: "Leave Management", icon: <Gavel />, component: <HRLeaveDashboard /> },
    { name: "HRPerformanceReviewDashboard", icon: <Dashboard />, component: <HRPerformanceReviewDashboard /> }
  ];

  const renderContent = () => {
    const selectedItem = menuItems.find(item => item.name === selectedMenu);
    if (selectedItem && selectedItem.component) {
      return selectedItem.component;
    }
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          {selectedMenu}
        </Typography>
        <Typography mt={2}>
          Feature for "{selectedMenu}" is currently under development.
        </Typography>
      </Box>
    );
  };

  // Drawer content component
  const drawerContent = (
    <Box
      sx={{
        width: { xs: "100%", md: 240 },
        backgroundColor: "#2C3E50",
        color: "#ECF0F1",
        height: "100%",
        overflowX: "hidden",
      }}
    >
      <Box sx={{ textAlign: "center", mb: 3, pt: 2 }}>
        <img
          src={hrmsDashboardImage}
          alt="HRMS Dashboard"
          style={{
            width: "60%",
            height: "auto",
            maxWidth: "120px",
            margin: "0 auto",
          }}
        />
      </Box>

      <Stack spacing={1.5} sx={{ px: 1 }}>
        {menuItems.map((item) => (
          <Button
            key={item.name}
            onClick={() => handleMenuSelect(item.name)}
            sx={{
              justifyContent: "flex-start",
              color: selectedMenu === item.name ? "#FFFFFF" : "#BDC3C7",
              backgroundColor:
                selectedMenu === item.name ? "#2980B9" : "transparent",
              padding: "10px 15px",
              borderRadius: 1,
              textTransform: "none",
              display: "flex",
              gap: 1,
              alignItems: "center",
              width: "100%",
              "&:hover": {
                backgroundColor: selectedMenu === item.name
                  ? "#2980B9"
                  : "rgba(41, 128, 185, 0.1)",
              },
              minWidth: 0,
            }}
          >
            <Box sx={{ minWidth: "24px" }}>
              {React.cloneElement(item.icon, {
                sx: { fontSize: "20px" }
              })}
            </Box>

            <Typography
              sx={{
                fontSize: "0.875rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.name}
            </Typography>
          </Button>
        ))}
      </Stack>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        minHeight: "100vh",
        width: "100%",
        overflowX: "hidden",
      }}
    >
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 240,
            backgroundColor: "#2C3E50",
            color: "#ECF0F1",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Sidebar */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          width: 240,
          flexShrink: 0,
        }}
      >
        {drawerContent}
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#ECF0F1",
          width: "100%",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: "15px 30px",
            bgcolor: "#34495E",
            color: "#ECF0F1",
            borderRadius: "0 12px 0 0",
            position: 'relative'
          }}
        >
          {/* Mobile menu button */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { xs: 'block', md: 'none' }
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h6" fontWeight="bold" noWrap>
              {selectedMenu}
            </Typography>
            <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
              <CalendarToday fontSize="small" sx={{ mr: 1 }} />
              {currentDate}
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }} />
          <AttendanceTimerWithoutTimers status={status} setStatus={setStatus} />

          {empId && <PushNotificationManager userId={empId} />}

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={handleNotificationClick}>
              <Badge badgeContent={notificationCount} color="secondary">
                <Notifications sx={{ color: "#FF5722" }} />
              </Badge>
            </IconButton>

            {userDetails && (
              <Tooltip title={getFullName()}>
                <Avatar
                  onClick={handleOpenProfile}
                  sx={{
                    cursor: "pointer",
                    width:34,
                    height: 34,
                    bgcolor: '#5C48A6',
                    color: 'white',
                    fontWeight: 'bold',
                    border: '2px solid #ECF0F1',
                    fontSize: '14px'
                  }}
                >
                  {getInitials(userDetails.first_name, userDetails.last_name)}
                </Avatar>
              </Tooltip>
            )}
          </Box>

          {/* Profile Modal */}
          <Modal open={openProfile} onClose={handleCloseProfile}>
            <Box sx={modalStyle}>
              <EditProfile onClose={handleCloseProfile} />
            </Box>
          </Modal>

          {/* Notifications Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleNotificationClose}
          >
            {notifications.length === 0 ? (
              <MenuItem>No notifications</MenuItem>
            ) : (
              notifications.map((note, index) => (
                <MenuItem key={index} onClick={handleNotificationClose}>
                  {typeof note === 'object' ? note.message : note}
                </MenuItem>
              ))
            )}
          </Menu>
        </Box>

        <Box sx={{
          flex: 1,
          p: 3,
          width: "100%",
          overflowX: "hidden"
        }}>
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default HRDashboard;